import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatAutocomplete,
  MatOption,
} from '@angular/material/autocomplete';
import { MatDialogRef, MatDialogClose } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Subject, debounceTime, startWith, takeUntil } from 'rxjs';
import { AxiosError } from 'axios';
import { endOfDay, format, subDays } from 'date-fns';
import {
  ClientsService,
  CreateClientParams,
  SelectedClient,
} from 'src/app/services/clients/clients.service';
import { EnumService } from 'src/app/services/enum/enum.service';
import {
  CompanySearchAutocompleteItem,
  CompanySearchService,
} from 'src/app/shared/company-search-service/company-search.service';
import { PayeePopupComponent } from '../../../shared/payee-popup/payee-popup.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PayeeButtonComponent } from '../../../shared/payee-buttons/payee-button/payee-button.component';
import { PayeeFormFieldComponent } from '../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeInputDirective } from '../../../shared/payee-input/payee-input.directive';
import { PayeeAutocompleteComponent } from '../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { PayeeDatepickerComponent } from '../../../shared/payee-form-fields/payee-datepicker/payee-datepicker.component';

@Component({
  selector: 'app-add-client-dialog',
  styleUrls: ['./add-client-dialog.component.scss'],
  templateUrl: './add-client-dialog.component.html',
  imports: [
    PayeePopupComponent,
    MatProgressSpinner,
    FormsModule,
    ReactiveFormsModule,
    PayeeButtonComponent,
    PayeeFormFieldComponent,
    PayeeInputDirective,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    PayeeAutocompleteComponent,
    PayeeDatepickerComponent,
    MatDialogClose,
    TranslateModule,
  ],
})
export class AddClientDialogComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private snackbar = inject(MatSnackBar);
  private clientsService = inject(ClientsService);
  private enumService = inject(EnumService);
  private translate = inject(TranslateService);
  private readonly destroy = new Subject<void>();
  private readonly companySearch$ = new Subject<string>();
  dialogRef =
    inject<MatDialogRef<AddClientDialogComponent, SelectedClient | undefined>>(
      MatDialogRef,
    );
  companySearchService = inject(CompanySearchService);

  readonly formGroup = this.fb.group({
    client_type: this.fb.control<'org' | 'house'>('org', {
      validators: [Validators.required, Validators.pattern(/org|house/)],
      nonNullable: true,
    }),
    name: this.fb.control('', {
      validators: [Validators.required, Validators.maxLength(255)],
      nonNullable: true,
    }),
    party_type_id: this.fb.control<string | null>(null),
    tax_number: this.fb.control('', {
      validators: Validators.maxLength(255),
      nonNullable: true,
    }),
    registration_number: this.fb.control('', {
      validators: Validators.maxLength(255),
      nonNullable: true,
    }),
    address: this.fb.group({
      country_iso: this.fb.control('HU', {
        validators: Validators.required,
        nonNullable: true,
      }),
      postcode: this.fb.control('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true,
      }),
      settlement: this.fb.control('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true,
      }),
      street: this.fb.control('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true,
      }),
    }),
    birth_date: this.fb.control<Date | string | null>(null),
    birth_place: this.fb.control('', {
      validators: Validators.maxLength(255),
      nonNullable: true,
    }),
    mother_name: this.fb.control('', {
      validators: Validators.maxLength(255),
      nonNullable: true,
    }),
  });

  readonly maxBirthDate = endOfDay(subDays(new Date(), 1));

  autocompleteOptions: CompanySearchAutocompleteItem[] = [];
  countryOptions: string[] = [];

  submitLoading = false;
  readonly initLoading = signal(true);

  get client_type() {
    return this.formGroup.controls.client_type;
  }
  get address() {
    return this.formGroup.controls.address;
  }
  get settlement() {
    return this.address.controls.settlement;
  }
  get postcode() {
    return this.address.controls.postcode;
  }
  get street() {
    return this.address.controls.street;
  }
  get country_iso() {
    return this.address.controls.country_iso;
  }
  get tax_number() {
    return this.formGroup.controls.tax_number;
  }
  get name() {
    return this.formGroup.controls.name;
  }
  get party_type_id() {
    return this.formGroup.controls.party_type_id;
  }
  get registration_number() {
    return this.formGroup.controls.registration_number;
  }
  get birth_date() {
    return this.formGroup.controls.birth_date;
  }
  get birth_place() {
    return this.formGroup.controls.birth_place;
  }
  get mother_name() {
    return this.formGroup.controls.mother_name;
  }

  async ngOnInit(): Promise<void> {
    this.client_type.valueChanges
      .pipe(takeUntil(this.destroy), startWith(this.client_type.value))
      .subscribe((v) => {
        if (v === 'org') {
          this.party_type_id.enable();
          this.tax_number.addValidators(Validators.required);
        } else {
          this.party_type_id.patchValue(null);
          this.party_type_id.disable();
          this.tax_number.removeValidators(Validators.required);
        }
        this.tax_number.updateValueAndValidity();
      });

    this.party_type_id.valueChanges
      .pipe(takeUntil(this.destroy), startWith(this.party_type_id.value))
      .subscribe((v) => {
        const isSoleEntrepreneur = v === '2-a';
        if (isSoleEntrepreneur) {
          this.birth_date.enable();
          this.birth_place.enable();
          this.mother_name.enable();
        } else {
          this.birth_date.disable();
          this.birth_place.disable();
          this.mother_name.disable();
        }
      });

    this.companySearch$
      .pipe(takeUntil(this.destroy), debounceTime(500))
      .subscribe(async (name) => {
        if (this.client_type.value !== 'org') {
          return;
        }

        try {
          this.autocompleteOptions =
            await this.companySearchService.getCompanySearchAutocompleteItems(
              name,
            );
        } catch (error) {
          console.error('Error while searching company', error);
        }
      });

    try {
      const [countries] = await Promise.all([this.enumService.getCountries()]);
      this.countryOptions = countries.map((c) => c.iso);
    } catch (error) {
      console.error('Error loading enums', error);
      this.snackbar.open(
        this.translate.instant('ERROR_MESSAGES.UNKNOWN_ERROR'),
        this.translate.instant('OK'),
        { duration: 5000 },
      );
    } finally {
      this.initLoading.set(false);
    }
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    this.companySearch$.complete();
  }

  onCompanySearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.companySearch$.next(value);
  }

  companySelected(event: MatAutocompleteSelectedEvent): void {
    const item = event.option.value as CompanySearchAutocompleteItem;
    this.formGroup.patchValue({
      name: item.short_name || item.name,
      address: {
        country_iso: item.country?.iso ?? 'HU',
        postcode: item.postcode ?? '',
        settlement: item.settlement ?? '',
        street: item.street ?? '',
      },
      tax_number: item.tax_number ?? '',
      party_type_id: item.party_type?.internal_id ?? null,
    });
  }

  readonly countryDisplayWith = (iso: string): string => {
    if (!iso) {
      return '';
    }
    return this.translate.instant(`COUNTRY_NAMES.${iso}`, {
      default: new Intl.DisplayNames(this.translate.currentLang, {
        type: 'region',
      }).of(iso),
    });
  };

  async submit(): Promise<void> {
    if (this.formGroup.invalid || this.submitLoading) {
      return;
    }

    try {
      this.submitLoading = true;

      const {
        client_type,
        name,
        party_type_id,
        tax_number,
        registration_number,
        address,
        birth_date: birthDateValue,
        birth_place,
        mother_name,
      } = this.formGroup.getRawValue();

      const birthDateStr = birthDateValue
        ? birthDateValue instanceof Date
          ? format(birthDateValue, 'yyyy-MM-dd')
          : birthDateValue
        : null;

      const params: CreateClientParams = {
        client_type,
        name,
        party_type_id,
        tax_number: tax_number || null,
        registration_number: registration_number || null,
        address: {
          country_iso: address.country_iso,
          postcode: address.postcode,
          settlement: address.settlement,
          street: address.street,
        },
        birth_date: birthDateStr,
        birth_place: birth_place || null,
        mother_name: mother_name || null,
      };

      const result = await this.clientsService.createClient(params);
      this.dialogRef.close(result);
      this.snackbar.open(
        this.translate.instant('CLIENT_ACCESS.ADD.SUCCESS'),
        this.translate.instant('OK'),
        { duration: 3000 },
      );
    } catch (error) {
      let message = this.translate.instant('ERROR_MESSAGES.UNKNOWN_ERROR');
      if (error instanceof AxiosError) {
        if (error.response?.status === 422) {
          const errorCode = error.response.data?.code;
          if (errorCode === 'not-unique') {
            this.formGroup.setErrors({ duplicate: true });
            this.formGroup.markAsDirty();
            return;
          }
        }

        if (error.response?.data?.message) {
          message = error.response.data.message;
        }
      }
      console.error('Error while creating client', error);
      this.snackbar.open(message, this.translate.instant('OK'), {
        duration: 5000,
      });
    } finally {
      this.submitLoading = false;
    }
  }
}
